"use client";

import { useEffect } from "react";

// Permanent fix for Next.js ChunkLoadError after a deploy. A stale browser tab
// requests JS/CSS chunks whose content-hash filenames no longer exist on the
// server after a rebuild, which Next surfaces as the generic
// "Application error: a client-side exception has occurred" page.
// We detect that specific class of error and reload once (rate-limited via
// sessionStorage so we never loop) so the tab pulls the fresh build. Any other
// (deterministic) error just shows a Reload button.
function isChunkLoadError(error?: {
	name?: string;
	message?: string;
}): boolean {
	if (!error) {
		return false;
	}
	const name = error.name || "";
	const message = error.message || "";
	return (
		name === "ChunkLoadError" ||
		/Loading chunk [\w-]+ failed/i.test(message) ||
		/Failed to load chunk|Loading CSS chunk [\w-]+ failed|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/i.test(
			message,
		)
	);
}

export default function GlobalError({
	error,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		if (isChunkLoadError(error)) {
			try {
				const KEY = "rb_chunk_reload_at";
				const last = Number(window.sessionStorage.getItem(KEY) || "0");
				// Reload at most once per 20s — recovers from a stale build without looping.
				if (Date.now() - last > 20000) {
					window.sessionStorage.setItem(KEY, String(Date.now()));
					window.location.reload();
				}
			} catch {
				window.location.reload();
			}
		}
	}, [error]);

	return (
		<html lang="en">
			<body
				style={{
					display: "flex",
					minHeight: "100vh",
					alignItems: "center",
					justifyContent: "center",
					fontFamily: "system-ui, -apple-system, sans-serif",
					background: "#ffffff",
					color: "#111111",
					margin: 0,
				}}
			>
				<div style={{ textAlign: "center", padding: "2rem", maxWidth: 440 }}>
					<h2 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: 8 }}>
						Something went wrong
					</h2>
					<p style={{ color: "#666666", fontSize: "0.9rem", marginBottom: 20 }}>
						The page failed to load. This can happen right after an update —
						reloading usually fixes it.
					</p>
					<button
						type="button"
						onClick={() => window.location.reload()}
						style={{
							padding: "0.5rem 1.25rem",
							borderRadius: 8,
							border: "none",
							background: "#111111",
							color: "#ffffff",
							fontSize: "0.9rem",
							cursor: "pointer",
						}}
					>
						Reload
					</button>
				</div>
			</body>
		</html>
	);
}
